import { useMemo } from "react";
import styled from "styled-components";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { Label } from "@sablier/v2-components/molecules";
import { Position } from "@sablier/v2-components/organisms";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Card as Base } from "~/client/components/molecules";
import { useStreamCurrent, useToken, useTransferField } from "~/client/hooks";
import type { ComponentProps } from "react";

const Wrapper = styled(Base)`
  width: 100%;
  *[data-component="card"] {
    border-radius: 8px;
  }
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;

    *[data-component="label"] {
      color: ${(props) => props.theme.colors.gray100};
    }
  }
`;

function Summary() {
  const { stream } = useStreamCurrent();
  const token = useToken({ token: stream?.token });
  const { t } = useT();
  const max = useTransferField("max");
  const toggle = useTransferField("toggle");

  const transferAll = useMemo(
    () =>
      stream?.depositAmount.humanized
        .minus(stream?.withdrawnAmount.humanized)
        .toString(),
    [stream],
  );

  const transferSplit = useMemo(
    () =>
      stream?.depositAmount.humanized
        .minus(stream?.streamedAmount.humanized)
        .toString(),
    [stream],
  );

  const items = useMemo(() => {
    const list: ComponentProps<typeof Position>["items"] = [
      {
        label: {
          value: `${t("words.streamed")}:`,
          icon: InformationCircleIcon,
          isIconLast: true,
          tooltip: {
            value: <>{t("tooltips.position.streamed")}</>,
            trigger: "hover",
            color: "dark000",
            arrow: true,
          },
        },
        progress: {
          percentage: stream?.streamedAmountPercentage?.toNumber(),
        },
        amount: {
          token,
          value: stream?.streamedAmount.humanized.toString(),
        },
      },
      {
        label: {
          value: `${t("words.withdrawn")}:`,
          icon: InformationCircleIcon,
          isIconLast: true,
          tooltip: {
            value: <>{t("tooltips.position.withdrawn")}</>,
            trigger: "hover",
            color: "dark000",
            arrow: true,
          },
        },
        progress: {
          percentage: stream?.withdrawnAmountPercentage?.toNumber(),
        },
        amount: {
          token,
          value: stream?.withdrawnAmount.humanized.toString(),
        },
      },
      {
        label: {
          value: `${_.capitalize(t("words.deposited"))}:`,
          icon: InformationCircleIcon,
          isIconLast: true,
          tooltip: {
            value: <>{t("tooltips.position.deposited")}</>,
            trigger: "hover",
            color: "dark000",
            arrow: true,
          },
        },
        amount: {
          token,
          value: stream?.depositAmount.humanized.toString(),
          isFormatted: true,
        },
        percentage: 100,
      },
      {
        isDivider: true,
      },
    ];

    if (toggle.field.value) {
      list.push({
        label: {
          value: `${t("structs.willWithdraw")}:`,
          icon: InformationCircleIcon,
          isIconLast: true,
          tooltip: {
            value: <>{t("tooltips.position.withdrawTransfer")}</>,
            trigger: "hover",
            color: "dark000",
            arrow: true,
          },
        },
        amount: {
          token,
          value: max.field.value,
        },
      });
    }

    list.push({
      label: {
        value: `${t("structs.willTransfer")}:`,
        icon: InformationCircleIcon,
        isIconLast: true,
        tooltip: {
          value: <>{t("tooltips.position.withdrawUnstreamed")}</>,
          trigger: "hover",
          color: "dark000",
          arrow: true,
        },
      },
      amount: {
        token,
        value: toggle.field.value ? transferSplit : transferAll,
      },
    });

    return list;
  }, [max, stream, token, t, toggle, transferAll, transferSplit]);

  return (
    <Wrapper isTopHidden>
      <Content>
        <Label value={t("modalCovers.transfer.summary")} />
        <Position items={items} />
      </Content>
    </Wrapper>
  );
}

export default Summary;
